import React, { useContext, useEffect } from 'react'
import formStyles from 'styles/form.module.scss'
import { Field } from 'react-final-form'
import Dropdown from 'react-dropdown'
import { getPostedByDropdownOptions } from 'api/transformers'
import Fields from 'components/common/Fields'
import { OnChange } from 'react-final-form-listeners'
import DeviceId from 'utils/detectDevice'
import { attachRecaptchaScript } from 'utils/recaptchaToken'

import {
  required,
  noSpaces,
  minValue,
  validEmail,
  uniqueEmail,
  inputHasErr,
  fieldHasErr,
  characterLimit,
  composeValidators,
} from 'utils/validations'
import { FormDataContext } from 'providers/FormData'

const showGenderOptions = value =>
  ['Self', 'Friend', 'Relative'].includes(value)

const showGenderError = (genderErr, postbyValue) =>
  genderErr && showGenderOptions(postbyValue)

const GenderFields = ({ postedby }) => {
  return (
    <>
      <Fields names={['gender']}>
        {({ gender }) => {
          const genderErr = fieldHasErr(gender)
          return (
            <div
              className={`${formStyles.genderEleWrap} ${
                genderErr ? formStyles.fullInputError : ''
              }`}
              style={{
                display: showGenderOptions(
                  DeviceId.isMobile()
                    ? postedby.input.value
                    : postedby.input.value.value
                )
                  ? 'block'
                  : 'none',
              }}
            >
              <Field
                type="radio"
                name="gender"
                value="Male"
                validate={composeValidators(required('gender'))}
              >
                {({ input, meta }) => (
                  <div className="form-check form-check-inline">
                    <input
                      data-testid="gender_male"
                      id="gender_male"
                      className="form-check-input"
                      type="radio"
                      {...input}
                    />

                    <label
                      className={`form-check-label ${
                        formStyles.layerRadioLable
                      }`}
                      htmlFor="gender_male"
                    >
                      Male
                    </label>
                  </div>
                )}
              </Field>
              <Field
                type="radio"
                name="gender"
                value="Female"
                validate={composeValidators(required('gender'))}
              >
                {({ input, meta }) => (
                  <div className="form-check form-check-inline">
                    <input
                      data-testid="gender_female"
                      id="gender_female"
                      className="form-check-input"
                      type="radio"
                      {...input}
                    />

                    <label
                      className={`form-check-label ${
                        formStyles.layerRadioLable
                      }`}
                      htmlFor="gender_female"
                    >
                      Female
                    </label>
                  </div>
                )}
              </Field>
            </div>
          )
        }}
      </Fields>
      <Fields names={['gender', 'postedby']}>
        {({ gender, postedby }) => {
          return showGenderError(
            fieldHasErr(gender),
            postedby.input.value.value
          ) ? (
            <p
              data-testid="gender_error"
              className={`${formStyles.error} col-md-12`}
            >
              {gender.meta.error}
            </p>
          ) : null
        }}
      </Fields>
    </>
  )
}

export const PostedBy = ({ options, meta, input }) => {
  return (
    <div className={`form-group col-md-12 ${formStyles.formEleWrap}`}>
      <label htmlFor="profileFor" className={formStyles.layerLable}>
        Create Profile for
      </label>
      {DeviceId.isMobile() && (
        <select
          className={`form-control ${inputHasErr(meta) &&
            formStyles.inputError} ${formStyles.selectMobile}`}
          {...input}
        >
          <option value="" hidden>
            Select
          </option>
          {options.map((option, index) => {
            return (
              <option value={option} key={index}>
                {option}
              </option>
            )
          })}
        </select>
      )}

      {DeviceId.isDesktop() && (
        <Dropdown
          controlClassName="postedby_selector"
          menuClassName="postedby_options"
          options={options}
          value={options[0]}
          placeholder={'Select'}
          className={`${inputHasErr(meta) && formStyles.inputError}`}
          {...input}
        />
      )}

      {meta.touched && meta.error && (
        <p data-testid="postedby_error" className={formStyles.error}>
          {meta.error}
        </p>
      )}
    </div>
  )
}

const SetupAccount = props => {
  useEffect(() => attachRecaptchaScript(), [])

  const { formData, loading } = useContext(FormDataContext)
  const postedbyOptions =
    !loading && getPostedByDropdownOptions(formData.postedBy)
  const genderOptionsMap = {
    Son: 'Male',
    Daughter: 'Female',
    Brother: 'Male',
    Sister: 'Female',
    Self: '',
    Relative: '',
    Friend: '',
  }
  return (
    <>
      <h2 className={formStyles.formTitle}>
        Let's set up your account, while
        <br />
        we find Matches for you!
      </h2>
      <div className="form-row">
        <Field
          name="email"
          validate={composeValidators(
            required('email'),
            validEmail,
            uniqueEmail
          )}
        >
          {({ input, meta }) => (
            <div className={`form-group col-md-12 ${formStyles.formEleWrap}`}>
              <label className={formStyles.layerLable}>
                Enter your email id
              </label>
              <input
                data-testid="email"
                className={`${inputHasErr(meta) &&
                  formStyles.inputError} form-control ${
                  formStyles.formTextFreeField
                }`}
                type="email"
                {...input}
              />
              {meta.touched && meta.error && (
                <p data-testid="email_error" className={formStyles.error}>
                  {meta.error}
                </p>
              )}
            </div>
          )}
        </Field>

        <Field
          name="password1"
          validate={composeValidators(
            required('password1'),
            minValue('password1', 4),
            characterLimit('Password', 20),
            noSpaces('password1')
          )}
        >
          {({ input, meta }) => (
            <div className={`form-group col-md-12 ${formStyles.formEleWrap}`}>
              <label className={formStyles.layerLable}>Create a password</label>
              <input
                data-testid="password1"
                className={`${inputHasErr(meta) &&
                  formStyles.inputError} form-control ${
                  formStyles.formTextFreeField
                }`}
                value={null}
                type="password"
                {...input}
              />
              {meta.touched && meta.error && (
                <p data-testid="password1_error" className={formStyles.error}>
                  {meta.error}
                </p>
              )}
            </div>
          )}
        </Field>

        <Field name="postedby">
          {fieldProps =>
            loading ? (
              'loading...'
            ) : (
              <PostedBy options={postedbyOptions} {...fieldProps} />
            )
          }
        </Field>
        <Fields names={['gender']}>
          {({ gender }) => {
            return (
              <OnChange name="postedby">
                {(value, previous) => {
                  if (value !== previous) {
                    let postedbyValue = DeviceId.isDesktop()
                      ? value.value
                      : value
                    gender.input.onChange(genderOptionsMap[postedbyValue])
                  }
                }}
              </OnChange>
            )
          }}
        </Fields>

        <Fields names={['postedby']}>{GenderFields}</Fields>
      </div>
    </>
  )
}

export default SetupAccount
